import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { PairedFile, pairedFilesAtom, sourceFilesByUrlAtom } from './atoms'
import { makeGetRequest } from 'infrastructure/api'
import { IMCourse } from './types'
import { useCallback, useMemo } from 'react'
import { doInBatches } from '../../utility'
import { parseCourse } from './ingestion/course'
import { useUploadIMCourse } from './utility'
import {
  globalDialogAtom,
  useWithErrorNotification,
} from 'shared/ui/GlobalDialog'

export function useMissingUrls() {
  const sourceFiles = useAtomValue(pairedFilesAtom)
  const sourceFileMap = useAtomValue(sourceFilesByUrlAtom)
  return useMemo(() => {
    return sourceFiles.reduce<string[]>((acc, { source: { URL } }) => {
      if (URL in sourceFileMap) return acc
      return [...acc, URL]
    }, [])
  }, [sourceFiles, sourceFileMap])
}

export function useGetAllMissingSourceFiles() {
  const [sourceFileMap, setSourceFiles] = useAtom(sourceFilesByUrlAtom)
  const missingUrls = useMissingUrls()
  const errorsSetter = useSetAtom(globalDialogAtom)
  return useCallback(async () => {
    const missingMap: Record<string, IMCourse> = {}
    const errors: string[] = []
    await doInBatches(missingUrls, async (url: string) => {
      const { result: course, error } = await makeGetRequest<IMCourse>({ url })
      if (error) errors.push(url, error)
      if (!course) return
      missingMap[url] = course
      parseCourse(course)
    })
    setSourceFiles({
      ...sourceFileMap,
      ...missingMap,
    })
    if (errors.length) {
      errorsSetter({ title: 'Request Errors', contents: errors })
    }
  }, [missingUrls, sourceFileMap, setSourceFiles])
}

export function useUpdatePreview() {
  const makeRequest = useWithErrorNotification(
    { errorTitle: 'Error Retrieving Preview' },
    makeGetRequest,
  )
  const uploader = useUploadIMCourse()
  const [sourceFiles, setSourceFiles] = useAtom(sourceFilesByUrlAtom)
  return useCallback(
    async (files: PairedFile) => {
      const sourceUrl = files.source.URL
      let theCourse: IMCourse | undefined = sourceFiles[sourceUrl]
      if (!theCourse) {
        theCourse = await makeRequest<IMCourse>({ url: sourceUrl })
        setSourceFiles((prev) => {
          if (!theCourse) return prev
          return {
            ...prev,
            [sourceUrl]: theCourse,
          }
        })
      }
      if (!theCourse) return
      return uploader(theCourse)
    },
    [uploader, sourceFiles, setSourceFiles, makeRequest],
  )
}

export function useDownloadSource() {
  const setErrors = useSetAtom(globalDialogAtom)
  const makeRequest = useWithErrorNotification(
    { errorTitle: 'Error Retrieving Source' },
    makeGetRequest,
  )
  const setter = useSetAtom(sourceFilesByUrlAtom)
  return useCallback(
    async ({ source: { URL } }: PairedFile) => {
      const theCourse = await makeRequest<IMCourse>({ url: URL })
      if (!theCourse) return
      setter((prev) => ({
        ...prev,
        [URL]: theCourse,
      }))
    },
    [setter, setErrors],
  )
}
